import React, { useState } from 'react';
import DailyCheckInHub from '@/components/checkin/DailyCheckInHub';
import DailyCheckInHistory from '@/components/DailyCheckInHistory';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BarChart3, Plus, Calendar as CalendarIcon } from 'lucide-react';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { useNavigate } from 'react-router-dom';
import { goBackOrHome } from '@/lib/navHelpers';
import { ILCard } from '@/components/il/ILCard';
import { useILScore } from '@/hooks/useILScore';
import { DateNavigation } from '@/components/shared/DateNavigation';
import { format } from 'date-fns';

type ViewMode = 'hub' | 'history' | 'calendar';

export default function DailyCheckInPage() {
  useNavigationHistory();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('hub');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const { todayScore, data: ilData, computeScore } = useILScore(30);

  const bucket = todayScore 
    ? (todayScore.il_score < 34 ? 'low' : todayScore.il_score < 67 ? 'moderate' : 'high')
    : 'moderate';

  const sparklineData = ilData?.slice(0, 30).reverse().map(d => ({
    date: d.date,
    score: d.il_score
  })) || [];

  return (
    <div className="min-h-screen bg-gradient-therapeutic">
      {/* Header */}
      <div className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => goBackOrHome(navigate)}
                className="hover:bg-primary/10 hover:text-primary transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-light text-foreground">Daily Wellness</h1>
                <p className="text-sm sm:text-base text-muted-foreground">Track your holistic wellbeing journey</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'hub' ? 'default' : 'outline'}
                onClick={() => setViewMode('hub')}
                size="sm"
                className={viewMode === 'hub' 
                  ? "gap-2 bg-gradient-primary shadow-md hover:shadow-lg transition-all" 
                  : "gap-2 hover:border-primary/60 hover:text-primary transition-colors"
                }
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Check-In</span>
              </Button>
              <Button
                variant={viewMode === 'history' ? 'default' : 'outline'}
                onClick={() => setViewMode('history')}
                size="sm"
                className={viewMode === 'history' 
                  ? "gap-2 bg-gradient-primary shadow-md hover:shadow-lg transition-all" 
                  : "gap-2 hover:border-primary/60 hover:text-primary transition-colors"
                }
              >
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">History</span>
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'outline'}
                onClick={() => setViewMode('calendar')}
                size="sm"
                className={viewMode === 'calendar' 
                  ? "gap-2 bg-gradient-primary shadow-md hover:shadow-lg transition-all" 
                  : "gap-2 hover:border-primary/60 hover:text-primary transition-colors"
                }
              >
                <CalendarIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Calendar</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-20">
        {todayScore && viewMode === 'hub' && (
          <div className="mb-6">
            <ILCard
              score={todayScore.il_score}
              bucket={bucket}
              contributors={todayScore.contributors}
              sparklineData={sparklineData}
              onRefresh={() => computeScore(undefined)}
            />
          </div>
        )}
        
      {viewMode === 'hub' ? (
        <>
          <DateNavigation 
            selectedDate={selectedDate} 
            onDateChange={setSelectedDate}
            className="mb-6"
          />
          <DailyCheckInHub selectedDate={format(selectedDate, 'yyyy-MM-dd')} />
        </>
      ) : viewMode === 'calendar' ? (
        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Track Any Day</h2>
            <DateNavigation 
              selectedDate={selectedDate} 
              onDateChange={(date) => {
                setSelectedDate(date);
                setViewMode('hub');
              }}
            />
          </Card>
        </div>
      ) : (
        <DailyCheckInHistory onStartNewCheckIn={() => setViewMode('hub')} />
      )}
      </div>
    </div>
  );
}